import React, { memo, useState, useCallback, useMemo } from 'react';
import { useGameMechanic } from 'src/hooks';
import { Col, Row } from 'react-bootstrap';
import { TDragDropTextGame, TDragDropTextGameOptions } from "./types";
import GameNotFound from '../../../not-found';
import { TouchBackend } from 'react-dnd-touch-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { checkIsMobile } from 'src/helpers';
import { DropItem, DragItem } from 'src/components';

// ========================================================================== //
// ================================= LOGIC ================================== //
// ========================================================================== //

/**
 * @description
 *    The calculation game component
 * @constructor
 */
function DragDropTextGame() {
  // Internal states
  const splitSentence = useCallback((sentence: string, index: number) => sentence.split('{{_}}')[index], []);
  const [completed, setCompleted] = useState<Record<number, TDragDropTextGameOptions>>({});
  const [moves, setMoves] = useState(0);
  const isMobile = checkIsMobile();
  /**
   * @description
   *    The game mechanic hooks that are necessary to load data and manage
   *    processes.
   */
  const {
    gameMechanic,
    changeCorrectAnswersPoints,
    points,
  } = useGameMechanic();

  if (!gameMechanic) {
    return <GameNotFound />;
  }

  const {
    sentence,
    sentences,
    missing_words,
    filePath,
  } = gameMechanic.content as unknown as TDragDropTextGame;

  function handleDrop(item: TDragDropTextGameOptions, index: number) {
    setMoves(prev => prev++);
    setCompleted(prev => ({ ...prev, [index]: { word: item.word, index: item.index } }));
  }

  function shuffleArray<T>(input: T[]): T[] {
    const arr = input.slice(); // copy so we don’t mutate the original
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  interface MissingWord {
    word: string;
    index: number;
  }

  function compareMissingWordsMaps(
    a: Record<number, MissingWord>,
    b: Record<number, MissingWord>
  ): boolean {
    const aKeys = Object.keys(a).map(Number).sort((x, y) => x - y);
    const bKeys = Object.keys(b).map(Number).sort((x, y) => x - y);

    // 1) same number of entries?
    if (aKeys.length !== bKeys.length) return false;

    // 2) same keys?
    for (let i = 0; i < aKeys.length; i++) {
      if (aKeys[i] !== bKeys[i]) return false;
    }

    // 3) each entry deeply equal?
    for (const key of aKeys) {
      const aw = a[key];
      const bw = b[key];
      if (!bw) return false;
      if (aw.index !== bw.index || aw.word !== bw.word) {
        return false;
      }
    }

    return true;
  }

  const isCompleted = useCallback((indexNumber: number, word:string) => {
    if (compareMissingWordsMaps(completed, missing_words)) {
      changeCorrectAnswersPoints(gameMechanic.points + points);
    }
    return Object.values(completed).filter(el => el.index === indexNumber && el.word === word).length;
  }, [completed]);
  const isGameFinished = useMemo(() => Object.keys(completed)?.length === missing_words?.length, [completed]);

  /**
   * Splits a sentence into segments, using each missing-word index as a split point.
   *
   * @param sentence     The full sentence string (words separated by spaces).
   * @param missingWords Array of objects { word: string; index: number }.
   *                     The `index` is the zero‐based position of the missing word in the sentence.
   * @returns            An array of string segments, in order, omitting the missing words themselves.
   */
  function splitByMissingWords(
    sentence: string,
    missingWords: Array<{ word: string; index: number }>
  ): string[] {
    // 1) Turn into words
    const words = sentence.split(' ');

    // 2) Extract & sort the missing‐word indices
    const splitIndices = missingWords
      .map(mw => mw.index)
      .sort((a, b) => a - b);

    const segments: string[] = [];
    let prev = 0;

    // 3) For each split point, take the words from `prev` up to (but not including) that index
    for (const idx of splitIndices) {
      if (idx > prev) {
        segments.push(words.slice(prev, idx).join(' '));
      } else {
        // if duplicate or out‐of‐order index, skip
      }
      prev = idx + 1; // skip the missing word itself
    }

    // 4) Finally, take any trailing words after the last missing word
    if (prev < words.length) {
      segments.push(words.slice(prev).join(' '));
    }

    return segments;
  }

  const parseSentence = (sentence: string) => {
    const parts = splitByMissingWords(sentence, missing_words);
    const ifFirstWord = missing_words.find(missingWord => missingWord.index === 0);
    const finalView: any[] = [];
    if (parts?.length > 1) {
      if (ifFirstWord) {
        finalView.push(<DropItem key={`${completed[0]?.word}-${0}`}
                                 onDrop={(item: TDragDropTextGameOptions) => handleDrop(item, 0)}>
          {completed[0]?.word}
        </DropItem>)
      }
      parts?.map((part: string, index: number) => {
        const currentIndex = ifFirstWord ? index + 1 : index;
        if (part?.length > 0) {
          finalView.push(part);
        }
        if (currentIndex <= parts?.length - 1) {
          if (ifFirstWord) {
            finalView.push(<DropItem key={`${completed[currentIndex]?.word}-${currentIndex}`}
              onDrop={(item: TDragDropTextGameOptions) => handleDrop(item, currentIndex)}>
              {completed[currentIndex]?.word}
            </DropItem>)
          } else {
            finalView.push(<DropItem key={`${completed[index]?.word}-${index}`}
                                     onDrop={(item: TDragDropTextGameOptions) => handleDrop(item, index)}>
              {completed[index]?.word}
            </DropItem>)
          }
        }
        return;
      })
    } else {
      finalView.push(splitSentence(sentence, 0));
      finalView.push(
        (<DropItem onDrop={(item: TDragDropTextGameOptions) => handleDrop(item, 0)}>
          {completed[0]?.word}
        </DropItem>)
      );
      finalView.push(splitSentence(sentence, 1));
    }

    return finalView;
  }

  return (
    <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
      {filePath ?  filePath.includes('video') ? (
          <video className='img-fluid' controls>
            <source src={filePath} type='video/mp4' key={Math.random()} />
          </video>)
        :
        ( <img
          src={filePath}
          alt={'drag_drop_text_image'}
          style={{ width: 500 }}
          key={Math.random()}
        />) : null}
      <div className='drag-text drag-drop' style={{ marginTop: 16 }}>

        <div className='sentences'>
          {sentence && !sentences && (
            <p>
              {parseSentence(sentence)}
            </p>
          )}
          {sentences && !sentence && sentences.map((selectedSentence, i) => (
            <p key={`sentence-${selectedSentence}-${i}`}>
              {splitSentence(selectedSentence, 0)}
              <DropItem onDrop={(item: TDragDropTextGameOptions) => handleDrop(item, i)}>
                {completed[i]?.word}
              </DropItem>
              {splitSentence(selectedSentence, 1)}
            </p>
          ))}
        </div>
        <Row>
          <Col md={12}>
            <div className='missing-words'>
              {
                shuffleArray(
                  missing_words.filter((w) => !isCompleted(w.index, w.word))
                ).map((w, i) => (
                    <DragItem
                      item={{ index: w.index, word: w.word, element:<span>{w.word}</span> }}
                      key={`${w.word}-${i}-missing`}
                    >
                      <span>{w.word}</span>
                    </DragItem>
                ))}
            </div>
          </Col>
        </Row>
      </div>
    </DndProvider>
  );
}

export default memo(DragDropTextGame);
